import { CaretLeft, X } from "@phosphor-icons/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  Children,
  isValidElement,
  useEffect,
  useMemo,
  type ReactElement,
  type ReactNode,
} from "react";

import { focusRing } from "../../foundations/a11y";
import { StackedViewTransition } from "../../foundations/motion/StackedViewTransition";
import { cn } from "../../lib/cn";
import { motion as motionTokens } from "../../tokens";

import type { SlideOverSide, SlideOverSize } from "./SlideOver";
import { SlideOverNavigationProvider } from "./SlideOverNavigationContext";
import {
  useSlideOverNavigator,
  type SlideOverNavigatorState,
} from "./useSlideOverNavigator";

const sizes: Record<SlideOverSize, string> = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
};

const isGooglePlacesSuggestionTarget = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest(".pac-container"));

const isPortaledAutocompleteTarget = (target: EventTarget | null) =>
  target instanceof Element &&
  Boolean(
    target.closest('[role="listbox"]') ||
      target.closest('[role="option"]') ||
      target.closest("[data-headlessui-portal]"),
  );

const shouldIgnoreOutsideInteraction = (target: EventTarget | null) =>
  isGooglePlacesSuggestionTarget(target) ||
  isPortaledAutocompleteTarget(target);

export type SlideOverNavigatorPanelProps = {
  id: string;
  title: string;
  description?: string;
  subHeader?: ReactNode;
  subHeaderClassName?: string;
  headerActions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
};

export type SlideOverNavigatorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPanel: string;
  navigator?: SlideOverNavigatorState;
  size?: SlideOverSize;
  side?: SlideOverSide;
  className?: string;
  children: ReactNode;
};

type SlideOverNavigatorPanelConfig = Omit<
  SlideOverNavigatorPanelProps,
  "children"
> & {
  children: ReactNode;
};

const PANEL_DISPLAY_NAME = "SlideOverNavigator.Panel";

function isPanelElement(
  child: ReactNode,
): child is ReactElement<SlideOverNavigatorPanelProps> {
  return (
    isValidElement(child) &&
    (child.type as { displayName?: string }).displayName === PANEL_DISPLAY_NAME
  );
}

function collectPanels(children: ReactNode): SlideOverNavigatorPanelConfig[] {
  return Children.toArray(children)
    .filter(isPanelElement)
    .map((child) => ({
      id: child.props.id,
      title: child.props.title,
      description: child.props.description,
      subHeader: child.props.subHeader,
      subHeaderClassName: child.props.subHeaderClassName,
      headerActions: child.props.headerActions,
      footer: child.props.footer,
      children: child.props.children,
    }));
}

function SlideOverNavigatorPanel(props: SlideOverNavigatorPanelProps) {
  void props;
  return null;
}

SlideOverNavigatorPanel.displayName = PANEL_DISPLAY_NAME;

export function SlideOverNavigator({
  open,
  onOpenChange,
  initialPanel,
  navigator: navigatorProp,
  size = "md",
  side = "right",
  className,
  children,
}: SlideOverNavigatorProps) {
  const reduce = useReducedMotion();
  const offscreen = side === "right" ? "100%" : "-100%";
  const internalNavigator = useSlideOverNavigator(initialPanel);
  const navigator = navigatorProp ?? internalNavigator;
  const panels = useMemo(() => collectPanels(children), [children]);
  const panelMap = useMemo(
    () => new Map(panels.map((panel) => [panel.id, panel])),
    [panels],
  );
  const activePanel = panelMap.get(navigator.activePanel);

  useEffect(() => {
    if (!open) {
      navigator.reset();
    }
  }, [navigator, open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      navigator.reset();
    }
    onOpenChange(nextOpen);
  };

  return (
    <SlideOverNavigationProvider value={navigator}>
      <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <AnimatePresence>
          {open && activePanel && (
            <DialogPrimitive.Portal forceMount>
              <DialogPrimitive.Overlay asChild forceMount>
                <motion.div
                  className="bg-scrim/40 fixed inset-0 z-40"
                  initial={reduce ? false : { opacity: 0 }}
                  animate={reduce ? undefined : { opacity: 1 }}
                  exit={reduce ? undefined : { opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                />
              </DialogPrimitive.Overlay>
              <DialogPrimitive.Content
                asChild
                forceMount
                onPointerDownOutside={(event) => {
                  if (shouldIgnoreOutsideInteraction(event.target)) {
                    event.preventDefault();
                  }
                }}
                onInteractOutside={(event) => {
                  if (shouldIgnoreOutsideInteraction(event.target)) {
                    event.preventDefault();
                  }
                }}
                onFocusOutside={(event) => {
                  if (shouldIgnoreOutsideInteraction(event.target)) {
                    event.preventDefault();
                  }
                }}
                {...(activePanel.description
                  ? {}
                  : { "aria-describedby": undefined })}
              >
                <motion.div
                  className={cn(
                    "bg-surface text-text shadow-modal fixed inset-y-0 z-50 flex w-full flex-col",
                    side === "right" ? "right-0" : "left-0",
                    sizes[size],
                    className,
                  )}
                  initial={reduce ? false : { x: offscreen }}
                  animate={reduce ? undefined : { x: 0 }}
                  exit={reduce ? undefined : { x: offscreen }}
                  transition={motionTokens.spring.default}
                >
                  <DialogPrimitive.Title className="sr-only">
                    {activePanel.title}
                  </DialogPrimitive.Title>
                  {activePanel.description ? (
                    <DialogPrimitive.Description className="sr-only">
                      {activePanel.description}
                    </DialogPrimitive.Description>
                  ) : null}
                  <div className="relative flex flex-1 flex-col overflow-hidden">
                    <DialogPrimitive.Close
                      aria-label="Close"
                      className={cn(
                        "text-text-tertiary hover:bg-fill absolute top-4 right-5 z-20 rounded-md p-1",
                        focusRing,
                      )}
                    >
                      <X size={20} aria-hidden="true" />
                    </DialogPrimitive.Close>
                    <StackedViewTransition
                      viewKey={navigator.activePanel}
                      depth={navigator.depth}
                      className="flex-1"
                      surfaceClassName="bg-surface"
                    >
                      <div className="flex h-full flex-col">
                        <div className="border-separator flex items-start justify-between gap-3 border-b px-5 py-4 pr-14">
                          <div className="flex min-w-0 items-center gap-2">
                            {navigator.canGoBack ? (
                              <button
                                type="button"
                                onClick={navigator.pop}
                                aria-label="Back"
                                className={cn(
                                  "text-text-tertiary hover:bg-fill -ml-1 shrink-0 rounded-md p-1",
                                  focusRing,
                                )}
                              >
                                <CaretLeft size={20} aria-hidden="true" />
                              </button>
                            ) : null}
                            <div className="flex min-w-0 flex-col gap-1">
                              <h2
                                aria-hidden="true"
                                className="text-heading text-text truncate"
                              >
                                {activePanel.title}
                              </h2>
                              {activePanel.description ? (
                                <p
                                  aria-hidden="true"
                                  className="text-body text-text-secondary"
                                >
                                  {activePanel.description}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          {activePanel.headerActions ? (
                            <div className="flex shrink-0 items-center gap-1">
                              {activePanel.headerActions}
                            </div>
                          ) : null}
                        </div>
                        {activePanel.subHeader ? (
                          <div
                            className={cn(
                              "border-separator border-b px-5 py-2.5",
                              activePanel.subHeaderClassName,
                            )}
                          >
                            {activePanel.subHeader}
                          </div>
                        ) : null}
                        <div className="flex-1 overflow-y-auto px-5 py-4">
                          {activePanel.children}
                        </div>
                        {activePanel.footer ? (
                          <div className="border-separator flex shrink-0 items-center justify-end gap-2 border-t px-5 py-4">
                            {activePanel.footer}
                          </div>
                        ) : null}
                      </div>
                    </StackedViewTransition>
                  </div>
                </motion.div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>
    </SlideOverNavigationProvider>
  );
}

SlideOverNavigator.Panel = SlideOverNavigatorPanel;
